import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const FONT_URLS = [
  'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/heebo/Heebo%5Bwght%5D.ttf',
  'https://raw.githubusercontent.com/google/fonts/main/ofl/heebo/Heebo%5Bwght%5D.ttf',
];

let fontCache: string | null = null;
let fontLoadAttempted = false;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

async function loadHebrewFont(): Promise<string | null> {
  if (fontLoadAttempted) return fontCache;

  for (const url of FONT_URLS) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength < 1000) continue; // Too small, probably an error page
      fontCache = arrayBufferToBase64(buffer);
      fontLoadAttempted = true;
      return fontCache;
    } catch {
      continue;
    }
  }

  fontLoadAttempted = true;
  return null;
}

function setupFont(doc: jsPDF): string {
  if (fontCache) {
    doc.addFileToVFS('Heebo-Regular.ttf', fontCache);
    doc.addFont('Heebo-Regular.ttf', 'Heebo', 'normal');
    doc.setFont('Heebo');
    return 'Heebo';
  }
  return 'helvetica';
}

export async function exportTableToPDF(
  pageTitle: string,
  headers: string[],
  rows: string[][],
  fileName: string
) {
  await loadHebrewFont();

  const doc = new jsPDF({ orientation: 'landscape' });
  const fontName = setupFont(doc);
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header: College name
  doc.setFontSize(20);
  doc.setTextColor(123, 31, 162);
  doc.text('\u200F' + 'מכללת מנארה', pageWidth / 2, 15, { align: 'center' });

  // Date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const today = new Date().toLocaleDateString('he-IL');
  doc.text('\u200F' + 'תאריך הפקה: ' + today, pageWidth / 2, 22, { align: 'center' });

  // Report title
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.text('\u200F' + pageTitle, pageWidth / 2, 30, { align: 'center' });

  // Table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 36,
    styles: {
      font: fontName,
      halign: 'right',
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [123, 31, 162],
      textColor: [255, 255, 255],
      halign: 'right',
      fontSize: 10,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [250, 245, 252],
    },
    theme: 'grid',
    margin: { top: 20 },
    didDrawPage: (data: any) => {
      // Add mini header on pages after the first
      if (data.pageNumber > 1) {
        const pFont = fontName;
        doc.setFont(pFont);
        doc.setFontSize(10);
        doc.setTextColor(123, 31, 162);
        doc.text('\u200F' + 'מכללת מנארה - ' + pageTitle, pageWidth / 2, 10, { align: 'center' });
      }
      // Footer with page numbers
      doc.setFont(fontName);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        '\u200F' + 'עמוד ' + data.pageNumber,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    },
  });

  doc.save(fileName);
}
